import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { assignInboxItem, type InboxItem } from "../api/inboxApi";
import type { ComponentWithSource } from "../api/componentsData";
import { buildCollectionPath, normalizeOrgSegment } from "../utils/collectionPaths";
import { CollectionFieldPicker } from "./CollectionFieldPicker";
import { getAssignableFields } from "./inboxFieldUtils";
import { useCreatableCollections } from "./useCreatableCollections";
import { useAuth } from "../auth/AuthContext";
import { rememberInboxField } from "./inboxAssignPrefs";

type AssignInboxItemDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inboxItem: InboxItem | null;
  onAssigned: (result: { collectionSlug: string; organization?: string }) => void;
  onStaleItem?: () => void;
};

export function AssignInboxItemDialog({
  open,
  onOpenChange,
  inboxItem,
  onAssigned,
  onStaleItem,
}: AssignInboxItemDialogProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const persistenceUserId =
    user?.uid || user?.info?.uid || user?.id || user?._id || null;
  const [collectionSlug, setCollectionSlug] = useState("");
  const [fieldName, setFieldName] = useState("");
  const [selectedComponent, setSelectedComponent] = useState<ComponentWithSource | null>(null);
  const [saving, setSaving] = useState(false);
  const { creatableCollections, loading: collectionsLoading } = useCreatableCollections(open);

  useEffect(() => {
    if (!open) {
      setCollectionSlug("");
      setFieldName("");
      setSelectedComponent(null);
    }
  }, [open]);

  const assignableFields = useMemo(
    () => getAssignableFields(selectedComponent),
    [selectedComponent]
  );

  const canSubmit =
    Boolean(inboxItem?.text?.trim()) &&
    Boolean(collectionSlug) &&
    Boolean(fieldName) &&
    assignableFields.length > 0 &&
    !saving;

  const handleAssign = async () => {
    if (!inboxItem || !selectedComponent || !fieldName) return;

    setSaving(true);
    try {
      const organization = normalizeOrgSegment(selectedComponent.organization || "") || undefined;
      const result = await assignInboxItem(inboxItem.uid, {
        collectionSlug: selectedComponent.slug,
        fieldName,
        organization,
      });

      const fieldLabel =
        assignableFields.find((field: any) => field.name === fieldName)?.label || fieldName;
      const collectionName = selectedComponent.name || selectedComponent.slug;
      const collectionPath = buildCollectionPath({
        slug: result.collectionSlug || selectedComponent.slug,
        organization: selectedComponent.organization,
      });

      toast.success(
        <div className="space-y-2">
          <div>
            Moved to {collectionName} → {fieldLabel}
          </div>
          <button
            type="button"
            className="text-sm underline"
            onClick={() => navigate(collectionPath)}
          >
            View collection
          </button>
        </div>
      );

      onAssigned({
        collectionSlug: result.collectionSlug || selectedComponent.slug,
        organization,
      });
      rememberInboxField(persistenceUserId, selectedComponent, fieldName);
      onOpenChange(false);
    } catch (error: unknown) {
      const err = error as Error & { status?: number };
      if (err.status === 404) {
        toast.error("Inbox item not found");
        onStaleItem?.();
        onOpenChange(false);
        return;
      }
      if (err.status === 403) {
        toast.error("You don't have permission to create items in this collection");
        return;
      }
      toast.error(err.message || "Could not move item");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Move to collection</DialogTitle>
          <DialogDescription>
            Create a new item in a collection using this inbox text.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Text preview</Label>
            <textarea
              value={inboxItem?.text || ""}
              readOnly
              rows={4}
              className={cn(
                "flex min-h-[96px] w-full resize-none rounded-md border border-input bg-muted/40 px-3 py-2 text-base shadow-sm md:text-sm"
              )}
            />
          </div>

          <CollectionFieldPicker
            collections={creatableCollections}
            collectionSlug={collectionSlug}
            fieldName={fieldName}
            collectionsLoading={collectionsLoading}
            persistenceUserId={persistenceUserId}
            restoreFromPrefs={open}
            onCollectionChange={(slug, component) => {
              setCollectionSlug(slug);
              setSelectedComponent(component);
            }}
            onFieldChange={setFieldName}
          />
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void handleAssign()} disabled={!canSubmit}>
            {saving ? "Moving..." : "Move to collection"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
